import {
  Badge,
  Box,
  Card,
  Flex,
  IconButton,
  Text,
  useBreakpointValue,
  Image,
  useToast,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LucideView, Plus } from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAppDispatch } from "../../store/hooks/hooks";
import { addToCart } from "../../store/cartSlice/cartSlice";
import { useGetProductsQuery } from "../../productApi/productAPI";
import { normalizeProductForCart } from "../../utils/normalizeCartProduct";

export interface CarouselProduct {
  id: string;
  _id?: string;
  productId?: string;

  userId?: string;
  sellerUserId?: string;
  ownerId?: string;
  sellerId?: string;
  sellerBusinessName?: string;
  businessName?: string;
  sellerName?: string;
  shopName?: string;
  dispatchWarehouseId?: string;

  title: string;
  image?: string;
  thumbnail?: string;
  imageUrl?: string;
  images?: string[];
  description: string;
  category?: string;
  price?: number;

  selectedVariants?: any[];
  selectedPrice?: number;
  selectedImage?: string;
  resolvedImage?: string;

  isPhysical?: boolean;
  weightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  bulky?: boolean;

  [key: string]: any;
}

interface ProductCarouselProps {
  products: CarouselProduct[];
  slidesToShow?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
}

const cardsBorderRadii = "8px";

const getProductId = (product: Partial<CarouselProduct>) =>
  String(product._id || product.id || product.productId || "").trim();

const ProductCarouselSmall: React.FC<ProductCarouselProps> = ({
  products,
  slidesToShow = { base: 3, md: 5, lg: 8 },
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const slideCount = useBreakpointValue(slidesToShow);
  const slideWidth = slideCount ? `${100 / slideCount}%` : "100%";

  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const { data: allProducts = [] } = useGetProductsQuery();

  const fullProductById = useMemo(() => {
    const map = new Map<string, any>();

    (allProducts || []).forEach((product: any) => {
      const id = getProductId(product);
      if (id) map.set(id, product);
    });

    return map;
  }, [allProducts]);

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";
  const brand = theme.brand;

  const pageText = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;
  const cardBg = theme.surface;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;

  const cardShadow = theme.shadowSm;
  const panelShadow = theme.shadowMd;

  const resolvedProducts = useMemo(() => {
    return (products || []).map((product) => {
      const productId = getProductId(product);
      const fullProduct = fullProductById.get(productId);

      const mergedProduct = {
        ...(fullProduct || {}),
        ...product,
      };

      const resolvedImage =
        product.selectedImage ||
        product.imageUrl ||
        (Array.isArray(product.images) ? product.images.find(Boolean) : "") ||
        product.image ||
        product.thumbnail ||
        fullProduct?.selectedImage ||
        fullProduct?.imageUrl ||
        (Array.isArray(fullProduct?.images)
          ? fullProduct.images.find(Boolean)
          : "") ||
        fullProduct?.image ||
        fullProduct?.thumbnail ||
        "";

      return {
        ...mergedProduct,
        id: productId,
        _id: productId,
        productId,
        resolvedImage,
      };
    });
  }, [products, fullProductById]);

  const updateArrowVisibility = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    updateArrowVisibility();

    const handleScroll = () => updateArrowVisibility();
    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", updateArrowVisibility);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateArrowVisibility);
    };
  }, [resolvedProducts.length, slideCount]);

  useEffect(() => {
    if (hasAutoScrolled || !scrollRef.current) return;
    if (!resolvedProducts.length) return;

    const container = scrollRef.current;
    const scrollAmount = container.offsetWidth;
    const totalScrolls = Math.ceil(resolvedProducts.length / (slideCount || 1));
    let currentStep = 0;

    const interval = setInterval(() => {
      if (currentStep < totalScrolls) {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        currentStep++;
      } else {
        container.scrollTo({ left: 0, behavior: "smooth" });
        clearInterval(interval);
        setHasAutoScrolled(true);
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [hasAutoScrolled, resolvedProducts.length, slideCount]);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = container.offsetWidth;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleOpenDetails = (id: string) => {
    navigate(`/product/${id}`);
  };

  const handleAddToCart = (
    product: CarouselProduct & { resolvedImage: string },
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    const productId = getProductId(product);
    const fullProduct = fullProductById.get(productId);

    const productForCart = {
      ...(fullProduct || {}),
      ...product,
    };

    const sellerId =
      productForCart.userId ||
      productForCart.sellerUserId ||
      productForCart.ownerId ||
      "";

    if (!sellerId) {
      toast({
        title: "Seller not found",
        description:
          "This product is missing seller information. Please open the product details and try again.",
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    dispatch(
      addToCart(
        normalizeProductForCart(productForCart, {
          id: productId,
          _id: productId,
          productId,
          image: product.resolvedImage,
          imageUrl: product.resolvedImage,
          selectedImage: product.resolvedImage,
          selectedVariants: Array.isArray(product.selectedVariants)
            ? product.selectedVariants
            : [],
          selectedPrice: Number(
            product.selectedPrice ?? product.price ?? productForCart.price ?? 0,
          ),
          price: Number(
            product.selectedPrice ?? product.price ?? productForCart.price ?? 0,
          ),
          quantity: 1,
        }),
      ),
    );

    toast({
      title: "Added to cart",
      description: "Product added to cart",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <Box
      position="relative"
      borderRadius={cardsBorderRadii}
      bg={cardBg}
      border={`1px solid ${borderColor}`}
      boxShadow={cardShadow}
      p={{ base: 2, md: 3 }}
    >
      <Box
        position="absolute"
        left={2}
        top={2}
        bottom={2}
        width={{ base: "30px", md: "80px" }}
        zIndex={1}
        bgGradient={
          isDark
            ? "linear(to-r, rgba(10,12,16,0.95) 0%, rgba(10,12,16,0.65) 30%, transparent 100%)"
            : "linear(to-r, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.75) 30%, transparent 100%)"
        }
        pointerEvents="none"
        display={showLeftArrow ? "flex" : "none"}
        borderRadius={cardsBorderRadii}
      />

      <Box
        position="absolute"
        right={2}
        top={2}
        bottom={2}
        width={{ base: "30px", md: "80px" }}
        zIndex={1}
        bgGradient={
          isDark
            ? "linear(to-l, rgba(10,12,16,0.95) 0%, rgba(10,12,16,0.65) 30%, transparent 100%)"
            : "linear(to-l, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.75) 30%, transparent 100%)"
        }
        pointerEvents="none"
        display={showRightArrow ? "flex" : "none"}
        borderRadius={cardsBorderRadii}
      />

      <IconButton
        aria-label="Scroll Left"
        icon={<ChevronLeftIcon boxSize={6} />}
        position="absolute"
        left={1}
        top="50%"
        transform="translateY(-50%)"
        zIndex={2}
        onClick={() => scroll("left")}
        variant="ghost"
        size="sm"
        bg={isDark ? "rgba(255,255,255,0.08)" : "rgba(24, 28, 42, 0.94)"}
        borderRadius={cardsBorderRadii}
        color="white"
        border={`1px solid ${borderStrong}`}
        backdropFilter="blur(8px)"
        boxShadow={panelShadow}
        display={showLeftArrow ? "flex" : "none"}
        _hover={{
          transform: "translateY(-50%) scale(1.03)",
          bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.78)",
        }}
        _active={{ transform: "translateY(-50%) scale(0.98)" }}
        transition="all 0.2s ease"
      />

      <IconButton
        aria-label="Scroll Right"
        icon={<ChevronRightIcon boxSize={6} />}
        position="absolute"
        right={1}
        top="50%"
        transform="translateY(-50%)"
        zIndex={2}
        onClick={() => scroll("right")}
        variant="ghost"
        size="sm"
        bg={isDark ? "rgba(255,255,255,0.08)" : "rgba(24, 28, 42, 0.94)"}
        borderRadius={cardsBorderRadii}
        color="white"
        border={`1px solid ${borderStrong}`}
        backdropFilter="blur(8px)"
        boxShadow={panelShadow}
        display={showRightArrow ? "flex" : "none"}
        _hover={{
          transform: "translateY(-50%) scale(1.03)",
          bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.78)",
        }}
        _active={{ transform: "translateY(-50%) scale(0.98)" }}
        transition="all 0.2s ease"
      />

      <Flex
        ref={scrollRef}
        overflowX="auto"
        scrollSnapType="x mandatory"
        gap="8px"
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {resolvedProducts.map((product, index) => (
          <Box
            key={`${product.id}-${index}`}
            flex={`0 0 ${slideWidth}`}
            scrollSnapAlign="start"
            h="380px"
            px={1}
          >
            <Card
              bg={cardBg}
              border={`1px solid ${borderColor}`}
              borderRadius={cardsBorderRadii}
              overflow="hidden"
              boxShadow={cardShadow}
              minWidth="160px"
              cursor="default"
              height="370px"
              position="relative"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: panelShadow,
                borderColor: borderStrong,
              }}
              transition="all 0.2s ease"
            >
              <Box position="relative">
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  h={{ base: "150px", md: "160px" }}
                  p={2}
                  bg="#fff"
                  borderBottom={`1px solid ${borderColor}`}
                  overflow="hidden"
                  position="relative"
                  cursor="pointer"
                  onClick={() => handleOpenDetails(product.id)}
                >
                  <Image
                    src={product.resolvedImage || undefined}
                    alt={product.title}
                    h="150px"
                    w="100%"
                    objectFit="contain"
                    borderRadius={cardsBorderRadii}
                    mb={3}
                    mt={{ base: 4, md: 0 }}
                    transition="transform 0.25s ease"
                    _hover={{ transform: "scale(1.06)" }}
                  />
                </Flex>

                <Flex
                  position="absolute"
                  top={3}
                  right={3}
                  bgGradient="linear(to-r, rgba(24,28,42,0.98), rgba(36,41,61,0.98), rgba(52,58,84,0.98))"
                  color="white"
                  px={3}
                  py={2}
                  alignItems="center"
                  borderRadius={cardsBorderRadii}
                  fontWeight="800"
                  fontSize="sm"
                  gap={2}
                  cursor="pointer"
                  boxShadow="0 2px 8px rgba(10,12,16,0.14)"
                  border="1px solid rgba(255,255,255,0.06)"
                  zIndex={3}
                  onClick={(e) => handleAddToCart(product, e)}
                  _hover={{
                    bgGradient:
                      "linear(to-r, rgba(28,32,48,0.99), rgba(40,46,68,0.99), rgba(56,63,92,0.99))",
                    transform: "translateY(-1px)",
                  }}
                  _active={{
                    transform: "translateY(0)",
                  }}
                  transition="all 0.22s ease"
                  sx={{
                    "&:hover .carousel-add-plus": {
                      transform: "rotate(90deg)",
                    },
                  }}
                >
                  <Box
                    className="carousel-add-plus"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="transform 0.25s ease"
                  >
                    <Plus size={17} />
                  </Box>
                  <Text fontSize="sm" lineHeight="1">
                    Add
                  </Text>
                </Flex>

                <Flex
                  position="absolute"
                  left={0}
                  bottom={0}
                  bgGradient={
                    theme.gradients?.primaryButton ||
                    "linear(to-r, teal.500, green.500)"
                  }
                  color="white"
                  px={3}
                  py={1.5}
                  alignItems="center"
                  borderTopRightRadius="20px"
                  borderBottomRightRadius="20px"
                  fontWeight="700"
                  minWidth="115px"
                  justifyContent="space-between"
                  onClick={() => handleOpenDetails(product.id)}
                  cursor="pointer"
                  boxShadow={theme.shadowSm}
                  border={`1px solid ${brand.electricBlue}33`}
                  _hover={{
                    filter: "brightness(1.03)",
                    transform: "translateX(2px)",
                  }}
                  transition="all 0.2s ease"
                  zIndex={2}
                >
                  <Text fontSize="sm">Details</Text>
                  <LucideView color="#fff" height="16px" />
                </Flex>
              </Box>

              <Box pt={{ base: 7, md: 8 }} p={{ base: 2, md: 4 }}>
                <Text
                  color={pageText}
                  fontSize={{ base: ".75rem", md: ".80rem" }}
                  fontWeight="800"
                  mb={1}
                  lineHeight="1rem"
                  noOfLines={1}
                >
                  {product.title}
                </Text>

                <Text
                  color={mutedText}
                  fontSize={{ base: "11px", md: "0.9rem" }}
                  mb={2}
                  lineHeight="1rem"
                  noOfLines={2}
                >
                  {product.description || product.title}
                </Text>

                <Text
                  fontWeight="800"
                  color={theme.success || "#38A169"}
                  lineHeight="1rem"
                  fontSize={{ base: "sm", md: "md" }}
                  mb={3}
                >
                  MWK{product.price}
                </Text>

                {/* <Badge
                  px="10px"
                  py="4px"
                  borderRadius="999px"
                  bg={isDark ? "rgba(72,187,120,0.14)" : "rgb(227 255 242)"}
                  color={isDark ? "green.300" : "green.700"}
                  border={`1px solid ${
                    isDark ? "rgba(72,187,120,0.22)" : "rgba(56,161,105,0.18)"
                  }`}
                  fontSize={{ base: "10px", md: "11px" }}
                  fontWeight="700"
                  textTransform="none"
                >
                  -20% off
                </Badge> */}
                <Badge
                  px="10px"
                  py="4px"
                  borderRadius={cardsBorderRadii}
                  bg={isDark ? "rgba(72,187,120,0.14)" : "rgb(227 255 242)"}
                  color={isDark ? "green.300" : "green.700"}
                  border={`1px solid ${
                    isDark ? "rgba(72,187,120,0.22)" : "rgba(56,161,105,0.18)"
                  }`}
                  fontSize={{ base: "10px", md: "11px" }}
                  fontWeight="700"
                  textTransform="none"
                >
                  {product.stockQty > 0 ? "In Stock" : "Out of Stock"}
                </Badge>

                <Text mt={3} fontSize="xs" color={subtleText} noOfLines={2}>
                  Tap details to view the full product page.
                </Text>
              </Box>
            </Card>
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

export default ProductCarouselSmall;
